import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import { observer } from 'mobx-react';

import { PitstopModal } from 'shared';
import { CreateCarForm } from './CreateCarForm';
import CarStore from 'stores/CarStore';
import CurrentUserStore from 'stores/CurrentUserStore';
import ShopStore from 'stores/ShopStore';
import { AppStore } from 'stores';

const CreateCarModal = ({ id }) => {
  const [state, setState] = useState({
    cars: [],
    extraFields: [],
    error: undefined,
    form: undefined,
  });

  const { cars, extraFields, error, form } = state;

  const columns = [
    { dataIndex: 'vin', title: 'VIN' },
    { dataIndex: 'baseMileage', title: 'Mileage' },
    { dataIndex: 'carName', title: 'Unit ID' },
    { dataIndex: 'scannerId', title: 'Device ID' },
  ];

  const extraColumns = [
    { dataIndex: 'make', title: 'Make' },
    { dataIndex: 'model', title: 'Model' },
    { dataIndex: 'year', title: 'Year' },
  ];

  const extraFieldsData = [
    { label: 'Make', name: 'make', required: true, type: 'text' },
    { label: 'Model', name: 'model', required: true, type: 'text' },
    { label: 'Year', name: 'year', required: true, type: 'number' },
  ];

  const addCars = async () => {
    let ignoreVinDecodingFailure = error === 'CannotDecodeVin';
    try {
      AppStore.addLoading('Vehicle is being added...');

      for (let i = 0; i < cars.length; i++) {
        await CarStore.addCar({
          ...cars[i],
          userId: CurrentUserStore.user.id,
          baseMileage: Number(cars[i].baseMileage),
          ignoreVinDecodingFailure: ignoreVinDecodingFailure,
        });
      }

      form && form.resetFields();
      clearCars();
      clearExtraFields();
    } catch (e) {
      setState((prev) => ({
        ...prev,
        extraFields: e.error === 'CannotDecodeVin' ? extraFieldsData : [],
        error: e.error,
      }));
      clearCars();
      throw e;
    } finally {
      AppStore.removeLoading('Vehicle is being added...');
    }
  };

  const clearCars = () => {
    setState((prev) => ({
      ...prev,
      cars: [],
    }));
  };

  const clearExtraFields = () => {
    setState((prev) => ({
      ...prev,
      extraFields: [],
      error: undefined,
    }));
  };

  const addToPending = (car, formInstance) => {
    setState((prev) => ({
      ...prev,
      cars: [
        ...prev.cars,
        { ...car, shopId: car.shopId || ShopStore.currentShop.id },
      ],
      form: formInstance,
    }));
  };

  let shops = [];
  if (CurrentUserStore.user.role === 'admin') {
    shops = Array.from(ShopStore.data.values());
  }

  let notifyMessage;
  switch (error) {
    case 'CannotDecodeVin':
      notifyMessage = 'Please Add Vehicle Manually';
      break;
    default:
      notifyMessage = undefined;
  }

  return (
    <PitstopModal
      id={id}
      title="Add Vehicle"
      width={800}
      okText="Add Vehicle"
      successMessage="Added Vehicles Successfully"
      failureMessage={error && error}
      notifyMessage={notifyMessage}
      onOk={addCars}
      onCancel={clearCars}
      form={form}
      clearExtraFields={clearExtraFields}
      okButtonProps={{ disabled: cars.length === 0 }}
    >
      <CreateCarForm
        onSubmit={addToPending}
        extraFields={extraFields}
        shops={shops}
      />
      <br />
      {cars.length !== 0 && (
        <Table
          columns={extraFields[0] ? columns.concat(extraColumns) : columns}
          dataSource={cars}
          rowKey="vin"
        />
      )}
    </PitstopModal>
  );
};

CreateCarModal.propTypes = {
  id: PropTypes.string.isRequired,
};

export default observer(CreateCarModal);
